import { Component, Inject, OnDestroy, OnInit, AfterViewInit, PLATFORM_ID } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { LoginServerService } from '../../services/login-server.service';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzModalService } from 'ng-zorro-antd/modal';
import { isPlatformBrowser } from '@angular/common'; // Import the helper function
import { InactivityServiceService } from '../../services/inactivity-service.service';
declare let bootstrap: any; // Remplacé var par let

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css'] // Fixed the property name from `styleUrl` to `styleUrls`
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {
  public isVisible = false;
  passwordVisible = false;
  public validateForm!: FormGroup;
  userEmail: string = '';
  public loading: boolean = false;
  password?: string;
  selectedImage: string = '';
  loginError: string = '';
  private modalCleanupHandler: (() => void) | null = null;

  constructor(
    private fb: FormBuilder,
    private authProvider: LoginServerService,
    private notification: NzNotificationService,
    private router: Router,
    private modalService: NzModalService,
    private inactivityServiceService:InactivityServiceService,
    @Inject(PLATFORM_ID) private platformId: Object // Inject platformId
  ) {}

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId) && this.authProvider.isAuthenticate()) {
      this.inactivityServiceService.startTimer();
    }
    this.validateForm = this.fb.group({
      userEmail: [
        '',
        [
          Validators.email,
          Validators.required,
          Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$'),
        ],
      ],
      userPassword: ['', [Validators.required]],
    });

    if (isPlatformBrowser(this.platformId) && this.authProvider.isAuthenticate()) {
      this.router.navigate(['/beekids/accueil']);
    }

    // BUG-001: Clean up Bootstrap modal backdrops when login modals are hidden
    if (isPlatformBrowser(this.platformId)) {
      this.modalCleanupHandler = () => {
        const backdrops = document.querySelectorAll('.modal-backdrop');
        backdrops.forEach((b) => b.remove());
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      };
      document.addEventListener('hidden.bs.modal', this.modalCleanupHandler);
    }
  }

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      // Clear error + form when login modal is opened
      const loginModal = document.getElementById('loginModal');
      if (loginModal) {
        loginModal.addEventListener('shown.bs.modal', () => {
          this.loginError = '';
          this.validateForm.reset();
        });
      }
    }
  }

  ngOnDestroy(): void {
    if (this.modalCleanupHandler) {
      document.removeEventListener('hidden.bs.modal', this.modalCleanupHandler);
    }
  }

  togglePasswordVisibility(): void {
    this.passwordVisible = !this.passwordVisible;
  }
  setImage(imageUrl: string) {
    this.selectedImage = imageUrl;
  }

  selectRoleAndOpenLogin(imageUrl: string) {
    this.selectedImage = imageUrl;
    this.loginError = '';
    this.validateForm.reset();
    // Hide role modal, then show login modal
    if (isPlatformBrowser(this.platformId)) {
      const roleModal = document.getElementById('loginModal');
      const bsRoleModal = roleModal ? bootstrap.Modal.getInstance(roleModal) : null;
      if (bsRoleModal) {
        bsRoleModal.hide();
        // Wait for role modal to fully hide before opening login modal
        const onHidden = () => {
          roleModal?.removeEventListener('hidden.bs.modal', onHidden);
          // Clean up any leftover backdrops
          document.querySelectorAll('.modal-backdrop').forEach(b => b.remove());
          document.body.classList.remove('modal-open');
          document.body.style.overflow = '';
          document.body.style.paddingRight = '';
          // Open login modal
          setTimeout(() => {
            const loginModalEl = document.getElementById('loginModalConnecte');
            if (loginModalEl) {
              const bsLoginModal = new bootstrap.Modal(loginModalEl);
              bsLoginModal.show();
            }
          }, 100);
        };
        roleModal?.addEventListener('hidden.bs.modal', onHidden);
      }
    }
  }

  submitForm(): void {
    this.loginError = '';
    if (!this.validateForm.valid) {
      this.handleValidationErrors();
      return;
    }
  
    this.loading = true;
    let auth = this.prepareAuthData();

    // BUG-006: Detect selected role from the image clicked
    const selectedRole = this.getSelectedRole();

    this.authProvider.Authenticate(auth.userEmail, auth.userPassword).subscribe(
      (res: any) => {
        this.loading = false;
        let actualRole = (res.roles && res.roles[0]) || res.role || localStorage.getItem('_sid') || '';

        // Formateur = Educateur: normalize backend role name
        if (actualRole === 'Formateur') {
          actualRole = 'Educateur';
          localStorage.setItem('_sid', actualRole);
        }

        // BUG-006: Check role matches the selected modal role
        if (selectedRole && actualRole !== selectedRole) {
          this.loginError = `Ce compte n'est pas associé au rôle "${selectedRole}". Veuillez choisir le bon profil.`;
          this.notification.create(
            'error',
            'Rôle incorrect',
            `Vous tentez de vous connecter en tant que "${selectedRole}" mais ce compte est "${actualRole}".`,
            { nzPlacement: 'bottomLeft', nzDuration: 6000 }
          );
          this.authProvider.logout();
          return;
        }

        if (isPlatformBrowser(this.platformId) && this.authProvider.isAuthenticate()) {
          this.handleSuccessfulLogin(actualRole);
        }
      },
      (error) => {
        this.loading = false;
        this.loginError = 'Email ou mot de passe incorrect. Veuillez réessayer.';
        this.notification.create(
          'error',
          'Échec de la connexion',
          'Veuillez vérifier vos informations d’identification.',
          { nzPlacement: 'bottomLeft', nzDuration: 6000 }
        );
      }
    );
  }
  
  /** BUG-006: Detect which role the user selected from the modal */
  private getSelectedRole(): string | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    // Map selectedImage to role
    if (this.selectedImage.includes('image1.png')) return 'Educateur';
    if (this.selectedImage.includes('image2.png')) return 'Parent';
    if (this.selectedImage.includes('image3.png')) return 'Directeur';
    return null;
  }

  /** Prépare les données d'authentification */
  private prepareAuthData() {
    let auth = { ...this.validateForm.value };
    auth.userEmail = auth.userEmail.toLowerCase();
    return auth;
  }
  
  /** Gère les erreurs de validation du formulaire */
  private handleValidationErrors(): void {
    Object.values(this.validateForm.controls).forEach((control) => {
      if (control.invalid) {
        control.markAsDirty();
        control.updateValueAndValidity({ onlySelf: true });
      }
    });
  }
  
  /** Gère la connexion réussie */
  private handleSuccessfulLogin(role: string): void {
    role = role || localStorage.getItem('_sid') || '';
  
    const modal = bootstrap.Modal.getInstance(document.getElementById('loginModalConnecte'));
    if (modal) modal.hide();

    // Clean up any remaining modal backdrops and body classes
    const backdrops = document.querySelectorAll('.modal-backdrop');
    backdrops.forEach((backdrop) => backdrop.remove());
    document.body.classList.remove('modal-open');
    document.body.style.overflow = '';
    document.body.style.paddingRight = '';
  
    if (role === 'Formateur') {
      role = 'Educateur';
      localStorage.setItem('_sid', role);
    }
  
    this.notification.create(
      'success',
      'Connexion réussie!',
      'Vous vous êtes connecté avec succès.',
      { nzPlacement: 'bottomLeft', nzDuration: 3000 }
    );
    
    this.redirectUser(role);
  }
  
  /** Redirige l'utilisateur selon son rôle */
  private redirectUser(role: string): void {
    const allowedRoles = ['Directeur', 'Parent', 'Educateur'];
    if (allowedRoles.includes(role)) {
      this.router.navigate(['/beekids/accueil']);
    }
  }
  
  

  openModalAndSendRequest(userEmail: string | null): void {
    // Close Bootstrap login modal before showing custom reset overlay
    if (isPlatformBrowser(this.platformId)) {
      const loginModal = document.getElementById('loginModalConnecte');
      if (loginModal) {
        const bsModal = bootstrap.Modal.getInstance(loginModal);
        if (bsModal) { bsModal.hide(); }
      }
    }
    this.isVisible = true;
  }

  handleCancel() {
    this.isVisible = false;
  }
}
